import { Box, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import StyledButton from '../../components/Common/StyledButton';
import Bookings from '../../components/Bookings/Bookings';
import CustomModal from '../../components/Common/CustomModal';
import BookingsModal from '../../components/Bookings/BookingsModal';
import CoachPageLayout from '../../components/CoachPageLayout/CoachPageLayout';
import {
  fetchCoachConfigurations,
  resetCalendarAuthURI,
  resetCheckTokenExists,
  resetSaveLinkedVideoCallPlatform,
  resetVideoPlatforAuthURI,
} from '../../redux/booking/bookingSlice';
import EmptyBookings from './EmptyBookings';
import createPermissionCheckers from '../../permissions-validation/createPermissionCheckers';
import { UserPermissionType } from '../../types/commonTypes';
import BookingSessions from './BookingSessions/BookingSessions';
import { toastStyles } from '../../utils/utils';

export default function ViewBookings() {
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const [showBookingModal, setShowBookingModal] = useState(false);
  const coachId = useAppSelector((state) => state.coach.coach.id);
  const coachConfigurations = useAppSelector((state) => state.booking.coachConfigurations);
  const linkToCopy = `${process.env.REACT_APP_AUTH_SERVICE}/u/${coachConfigurations.url}`;
  const coachConfigurationsLoading = useAppSelector(
    (state) => state.booking.coachConfigurationsLoading
  );
  const dispatch = useAppDispatch();
  const copyToClipboard = () => {
    navigator.clipboard.writeText(linkToCopy);
    toast.success(t('common.copied'), toastStyles.success);
  };
  const permissionCheckers = createPermissionCheckers();
  const { checkPermissions } = permissionCheckers;
  const { i18n } = useTranslation();

  useEffect(() => {
    if (!coachId) return;
    dispatch(fetchCoachConfigurations({ coachId }));
  }, [coachId]);

  const handleBookingModalOpen = () => {
    setShowBookingModal(true);
    if (!coachId) return;
    dispatch(fetchCoachConfigurations({ coachId }));
  };
  const closeModal = () => {
    setShowBookingModal(false);
    dispatch(resetCheckTokenExists());
    dispatch(resetCalendarAuthURI());
    dispatch(resetVideoPlatforAuthURI());
    dispatch(resetSaveLinkedVideoCallPlatform());
  };

  if (coachConfigurationsLoading) {
    return (
      <CoachPageLayout>
        <EmptyBookings />
      </CoachPageLayout>
    );
  }

  if (coachConfigurations.hasBookings && coachConfigurations.activeStep < 0) {
    return <BookingSessions />;
  }

  return (
    <Box>
      <CoachPageLayout>
        {coachConfigurations.activeStep < 0 && (
          <Bookings
            key={i18n.language}
            title={`${t('bookings.viewbookings.emptyBookingsTitle')} `}
            description={t('bookings.viewbookings.emptyBookingsDesc')}
            icon={<img src="/assets/icons/bookings-empty.svg" alt="bookings" />}
            component={
              <Box
                sx={{
                  padding: '16px 24px',
                  borderRadius: '10px',
                  justifyContent: 'center',
                  alignContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'row',
                  background: '#7CAEBE33',
                  overflowX: 'auto',
                  overflowY: 'auto',
                }}
              >
                <Tooltip
                  title={linkToCopy}
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: 'primary.main',
                        borderRadius: '5px',
                        padding: '8px 12px',
                      },
                    },
                  }}
                >
                  <Typography
                    variant="bodyBold"
                    px={1}
                    color="primary"
                    maxWidth={isMediumScreen ? 300 : 500}
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      direction: 'ltr',
                    }}
                  >
                    {linkToCopy}
                  </Typography>
                </Tooltip>
                <button
                  type="button"
                  style={{ background: 'transparent', border: 'none' }}
                  onClick={copyToClipboard}
                >
                  <img
                    src="/assets/icons/bookings-copy.svg"
                    alt="bookings"
                    style={{ cursor: 'pointer' }}
                  />
                </button>
              </Box>
            }
            activeStep={coachConfigurations.activeStep}
          />
        )}
        {coachConfigurations.activeStep === 0 && (
          <Bookings
            key={i18n.language}
            title={`${t('bookings.viewbookings.getStartedTitle')} `}
            titlesub={t('bookings.viewbookings.titleseconds')}
            description={t('bookings.viewbookings.getStartedDesc')}
            icon={<img src="/assets/icons/bookings.svg" alt="bookings" />}
            component={
              <Box width="100%" sx={{ alignContent: 'center', textAlign: 'center' }}>
                {checkPermissions([
                  UserPermissionType.BOOKINGS_MODULE_VIEW_COACH_CONFIGURATIONS,
                  UserPermissionType.BOOKINGS_MODULE_MANAGE_COACH_CONFIGURATIONS,
                ]) && (
                  <StyledButton onClick={handleBookingModalOpen} paddingY={12}>
                    {t('bookings.viewbookings.getStarted')}
                  </StyledButton>
                )}
              </Box>
            }
            activeStep={coachConfigurations.activeStep}
          />
        )}
        {coachConfigurations.activeStep > 0 && (
          <Bookings
            key={i18n.language}
            title={t('bookings.continue.title')}
            description={t('bookings.continue.description')}
            icon={<img src="/assets/icons/bookings.svg" alt="bookings" />}
            component={
              <Box width="100%" sx={{ alignContent: 'center', textAlign: 'center' }}>
                {checkPermissions([
                  UserPermissionType.BOOKINGS_MODULE_VIEW_COACH_CONFIGURATIONS,
                  UserPermissionType.BOOKINGS_MODULE_MANAGE_COACH_CONFIGURATIONS,
                ]) && (
                  <StyledButton onClick={handleBookingModalOpen} paddingY={12}>
                    {t('bookings.continue.button')}
                  </StyledButton>
                )}
              </Box>
            }
            activeStep={coachConfigurations.activeStep}
          />
        )}
      </CoachPageLayout>
      {showBookingModal &&
        checkPermissions(UserPermissionType.BOOKINGS_MODULE_VIEW_COACH_CONFIGURATIONS) && (
          <CustomModal isOpen={showBookingModal} xs={11}>
            <BookingsModal closeModel={closeModal} />
          </CustomModal>
        )}
    </Box>
  );
}
